import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import css from "./style.scss";
import uparrow from "../../assets/images/uparrow.svg";
import downarrow from "../../assets/images/downarrow.svg";

const CurrentLeadsDropdown = ({
  items,
  label,
  placeholder,
  onSelect,
  required = false,
  value = null,
  noDataMessage = null,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(value);
  const [searchValue, setSearchValue] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});

  const containerRef = useRef(null);
  const dropdownInputRef = useRef(null);
  const dropdownListRef = useRef(null);

  useEffect(() => {
    setSelectedItem(value?.name ? value : null);
  }, [value]);

  const updatePosition = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    setDropdownStyle({
      position: "fixed",
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
      zIndex: 10000,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  const getScrollParent = (el) => {
    let node = el;
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      if (style.overflowY === "auto" || style.overflowY === "scroll") {
        return node;
      }
      node = node.parentElement;
    }
    return document.body;
  };

  useEffect(() => {
    if (!isOpen) return;

    const stopScroll = (e) => {
      e.preventDefault();
    };

    const scrollParent = getScrollParent(containerRef.current);

    scrollParent?.addEventListener("wheel", stopScroll, { passive: false });
    scrollParent?.addEventListener("touchmove", stopScroll, { passive: false });

    return () => {
      scrollParent?.removeEventListener("wheel", stopScroll);
      scrollParent?.removeEventListener("touchmove", stopScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        !containerRef.current?.contains(e.target) &&
        !dropdownListRef.current?.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const filteredItems = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    return term
      ? items.filter((i) => i.name.toLowerCase().includes(term))
      : items;
  }, [searchValue, items]);

  return (
    <div className={css.autocompleteContainer} ref={containerRef}>
      <label className={css.autocompleteLabel}>
        {label} {required && <span>*</span>}
      </label>

      <div className={`${css.autocompleteInputWrapper} ${isOpen ? css.open : ""}`}>
        <input
          ref={dropdownInputRef}
          value={
            isOpen
              ? searchValue
              : selectedItem?.name || placeholder || "No Status"
          } onFocus={() => {
            if(disabled) return;
            setSearchValue("");
            setIsOpen(true);
          }}
          onChange={(e) => {
            if(disabled) return;
            setSearchValue(e.target.value);
            setIsOpen(true);
          }}
          placeholder={placeholder}
          className={css.autocompleteInput}
          disabled={disabled}
        />

        <button type="button" className={css.dropdownArrow} onClick={() => { if(disabled) return; setIsOpen((v) => !v); }} disabled={disabled}>
          <img src={isOpen ? uparrow : downarrow} className={css.arrowIcon} alt="arrow" />
        </button>
      </div>

      {isOpen &&
        createPortal(
          <ul ref={dropdownListRef} className={css.autocompleteList} style={dropdownStyle}>
            {filteredItems.length ? (
              filteredItems.map((item) => (
                <li
                  key={item.id}
                  className={css.autocompleteItem}
                  onMouseDown={() => {
                    setSelectedItem(item);
                    setIsOpen(false);
                    setSearchValue("");
                    onSelect?.(item);
                  }}
                >
                  {item.name}
                </li>
              ))
            ) : (
              noDataMessage && <div>{noDataMessage}</div>
            )}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default CurrentLeadsDropdown;
